import { ComponentBase } from "../core/ComponentBase.js";

export class SkillConfigurationView extends ComponentBase {
    static styles = `
        .skill-config-container {
            display: flex;
            height: 100%;
            background-color: var(--bs-body-bg);
        }
        .config-sidebar {
            width: 260px;
            border-right: 1px solid var(--bs-border-color);
            display: flex;
            flex-direction: column;
            background-color: var(--bs-tertiary-bg);
        }
        .config-content-area {
            flex: 1;
            overflow-y: auto;
            padding: 2rem;
            position: relative;
        }
        .config-nav-item {
            padding: 12px 20px;
            cursor: pointer;
            border-left: 3px solid transparent;
            color: var(--bs-body-color);
            display: flex;
            align-items: center;
            gap: 10px;
            transition: all 0.2s;
        }
        .config-nav-item:hover { background-color: var(--bs-secondary-bg); }
        .config-nav-item.active {
            background-color: var(--bs-body-bg);
            border-left-color: var(--bs-primary);
            font-weight: 600;
            color: var(--bs-primary);
        }
        .config-header {
            padding: 1rem;
            border-bottom: 1px solid var(--bs-border-color);
        }
        .skill-config-tab-content {
            max-width: 800px;
            margin: 0 auto;
            animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(5px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 768px) {
            .skill-config-container { flex-direction: column; }
            .config-sidebar { width: 100%; height: auto; flex-direction: column; border-right: none; border-bottom: 1px solid var(--bs-border-color); }
            .config-nav-list { flex-direction: row !important; overflow-x: auto; flex-wrap: nowrap; scrollbar-width: none; }
            .config-nav-list::-webkit-scrollbar { display: none; }
            .config-nav-item { white-space: nowrap; border-left: none; border-bottom: 3px solid transparent; }
            .config-nav-item.active { border-left: none; border-bottom-color: var(--bs-primary); }
            .config-content-area { padding: 1rem; }
        }
    `;

    constructor(config) {
        super(config);
        this.skillId = null;
        this.skillData = {};
        this.originalData = "{}";
        this.isSpecialSub = false;
        this.isReadOnly = false;

        this.skillListRoute = this.config.skillListRoute || '/user/settings/skills';

        this.activeTabId = 'general';
        this.tabs = [
            { id: 'general', label: 'General', icon: 'bi-info-circle' },
            { id: 'engine', label: 'Engine Config', icon: 'bi-cpu' },
            { id: 'prompt', label: 'Prompt Template', icon: 'bi-file-text' },
            { id: 'tools', label: 'Allowed Tools', icon: 'bi-tools' }
        ];

        this.toolsInput = null;
        this.applyStyles();
    }

    async initAfterLogin() {
        try {
            const res = await this.appClient.rpcClient.call('SkillRpc.isSpecialSubscription');
            this.isSpecialSub = res.isSpecial;
        } catch (e) { this.log.error(e); }
    }

    async refresh() {
        await this.loadDataBasedOnRoute();
    }

    async loadDataBasedOnRoute() {
        const queryParams = this.appClient.layoutEngine.getQueryParams();
        this.skillId = queryParams.skillId;
        this.isCreateMode = !this.skillId;

        if (this.isCreateMode) {
            this.skillData = {
                name: '', description: '', engineType: 'StandardSkillEngine',
                engineConfig: { maxTurns: 15, temperature: 0.7 },
                promptTemplate: 'You are a helpful assistant executing a skill.\n\nGoal: {{jobState.goal}}',
                allowedTools: [], isGlobal: false
            };
            this.isReadOnly = false;
        } else {
            try {
                // If it's a file-based skill, the ID might just be the name
                const allSkills = await this.appClient.rpcClient.call('SkillRpc.listSkills');
                const skill = allSkills.data.find(s => s._id === this.skillId || s.name === this.skillId);

                if (!skill) throw new Error("Skill not found");
                this.skillData = skill;

                // File skills are read-only. Global skills are read-only if not in special sub.
                this.isReadOnly = (skill._source === 'file') || (skill.isGlobal && !this.isSpecialSub);

            } catch (e) {
                this.appClient.notifier.error("Failed to load skill: " + e.message);
                this.handleBackNavigation();
                return;
            }
        }

        this.originalData = JSON.stringify(this.skillData);
        this.activeTabId = 'general';
        if (this.container) await this.render();
    }

    async handleBackNavigation() {
        if (JSON.stringify(this.skillData) !== this.originalData) {
            const confirmed = await this.appClient.notifier.confirm(this.t("You have unsaved changes. Are you sure you want to leave?"));
            if (!confirmed) return;
        }
        this.appClient.layoutEngine.navigate(this.skillListRoute);
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'skill-config-container';
        }
        this.container.innerHTML = '';

        // Sidebar
        const sidebar = document.createElement('div');
        sidebar.className = 'config-sidebar';

        const header = document.createElement('div');
        header.className = 'config-header';
        let badges = '';
        if (this.skillData.isGlobal) badges += '<span class="badge bg-primary ms-2">Global</span>';
        if (this.isReadOnly) badges += '<span class="badge bg-secondary ms-1">Read-Only</span>';

        header.innerHTML = `<h5 class="m-0 text-truncate">${this.isCreateMode ? 'New Skill' : (this.skillData.name || 'Unnamed')} ${badges}</h5>`;
        sidebar.appendChild(header);

        const navList = document.createElement('div');
        navList.className = 'config-nav-list d-flex flex-column w-100';
        this.tabs.forEach(tab => {
            const item = document.createElement('div');
            item.className = `config-nav-item ${this.activeTabId === tab.id ? 'active' : ''}`;
            item.innerHTML = `<i class="bi ${tab.icon}"></i> ${this.t(tab.label)}`;
            item.onclick = () => this.switchTab(tab.id);
            navList.appendChild(item);
        });
        sidebar.appendChild(navList);

        // Content Area
        const contentArea = document.createElement('div');
        contentArea.className = 'config-content-area';

        const toolbar = document.createElement('div');
        toolbar.className = 'd-flex justify-content-end mb-3 gap-2';
        toolbar.innerHTML = `
            <button class="btn btn-outline-secondary" id="btn-cancel">${this.t('Back')}</button>
            <button class="btn btn-primary" id="btn-save" ${this.isReadOnly ? 'disabled' : ''}><i class="bi bi-save"></i> ${this.t('Save')}</button>
        `;
        contentArea.appendChild(toolbar);

        const tabContainer = document.createElement('div');
        tabContainer.id = 'active-tab-container';
        contentArea.appendChild(tabContainer);

        this.container.appendChild(sidebar);
        this.container.appendChild(contentArea);

        toolbar.querySelector('#btn-cancel').onclick = () => this.handleBackNavigation();
        if (!this.isReadOnly) {
            toolbar.querySelector('#btn-save').onclick = () => this.saveSkill();
        }

        await this.renderActiveTab(tabContainer);
        return this.container;
    }

    async switchTab(tabId) {
        if (this.activeTabId === tabId) return;
        // Save current tab data before switching
        this.extractCurrentTabData();
        this.activeTabId = tabId;

        const items = this.container.querySelectorAll('.config-nav-item');
        items.forEach((el, idx) => {
            if (this.tabs[idx].id === tabId) el.classList.add('active');
            else el.classList.remove('active');
        });

        const tabContainer = this.container.querySelector('#active-tab-container');
        if (tabContainer) {
            await this.renderActiveTab(tabContainer);
        }
    }

    extractCurrentTabData() {
        if (this.isReadOnly) return;
        const container = this.container.querySelector('#active-tab-container');
        if (!container) return;

        if (this.activeTabId === 'general') {
            this.skillData.name = container.querySelector('#skill-name').value;
            this.skillData.description = container.querySelector('#skill-desc').value;
            if (this.isSpecialSub) {
                this.skillData.isGlobal = container.querySelector('#skill-global').checked;
            }
        } else if (this.activeTabId === 'engine') {
            this.skillData.engineType = container.querySelector('#engine-type').value;
            this.skillData.engineConfig.llm = container.querySelector('#engine-llm').value;
            this.skillData.engineConfig.maxTurns = parseInt(container.querySelector('#engine-turns').value, 10);
        } else if (this.activeTabId === 'prompt') {
            this.skillData.promptTemplate = container.querySelector('#skill-prompt').value;
        } else if (this.activeTabId === 'tools' && this.toolsInput) {
            this.skillData.allowedTools = this.toolsInput.getValue();
        }
    }

    async renderActiveTab(targetContainer) {
        targetContainer.innerHTML = '';
        const content = document.createElement('div');
        content.className = 'skill-config-tab-content';
        const disabledAttr = this.isReadOnly ? 'disabled' : '';

        if (this.activeTabId === 'general') {
            content.innerHTML = `
                <h4 class="mb-4">General Information</h4>
                <div class="mb-3">
                    <label class="form-label">Skill Name (No spaces)</label>
                    <input type="text" class="form-control" id="skill-name" value="${this.skillData.name}" ${disabledAttr}>
                </div>
                <div class="mb-3">
                    <label class="form-label">Description (Used by AI to select this skill)</label>
                    <textarea class="form-control" id="skill-desc" rows="3" ${disabledAttr}>${this.skillData.description}</textarea>
                </div>
                ${this.isSpecialSub ? `
                <div class="form-check form-switch mt-4">
                    <input class="form-check-input" type="checkbox" id="skill-global" ${this.skillData.isGlobal ? 'checked' : ''} ${disabledAttr}>
                    <label class="form-check-label fw-bold">Make Global (Available to all tenants)</label>
                </div>` : ''}
            `;
        }
        else if (this.activeTabId === 'engine') {
            content.innerHTML = `
                <h4 class="mb-4">Engine Configuration</h4>
                <div class="mb-3">
                    <label class="form-label">Engine Type</label>
                    <select class="form-select" id="engine-type" ${disabledAttr}>
                        <option value="StandardSkillEngine" ${this.skillData.engineType === 'StandardSkillEngine' ? 'selected' : ''}>Standard Skill Engine</option>
                        <option value="ComputerControlEngine" ${this.skillData.engineType === 'ComputerControlEngine' ? 'selected' : ''}>Computer Control Engine</option>
                    </select>
                </div>
                <div class="mb-3">
                    <label class="form-label">Override LLM Model (Optional)</label>
                    <input type="text" class="form-control" id="engine-llm" placeholder="e.g., gpt-4o" value="${this.skillData.engineConfig?.llm || ''}" ${disabledAttr}>
                </div>
                <div class="mb-3">
                    <label class="form-label">Max Turns (Safety limit)</label>
                    <input type="number" class="form-control" id="engine-turns" value="${this.skillData.engineConfig?.maxTurns || 15}" ${disabledAttr}>
                </div>
            `;
        }
        else if (this.activeTabId === 'prompt') {
            content.innerHTML = `
                <h4 class="mb-4">Prompt Template</h4>
                <p class="text-muted small">Use Mustache syntax <code>{{variable}}</code>. The engine will inject <code>{{jobState.goal}}</code> and other context automatically.</p>
                <textarea class="form-control font-monospace" id="skill-prompt" rows="15" style="font-size: var(--ab-text-sm);" ${disabledAttr}>${this.skillData.promptTemplate}</textarea>
            `;
        }
        else if (this.activeTabId === 'tools') {
            content.innerHTML = `
                <h4 class="mb-4">Allowed Tools</h4>
                <p class="text-muted small">Select which tools this skill is allowed to use during its execution loop.</p>
                <div id="tools-input-container"></div>
            `;

            if (!this.toolsInput) {
                this.toolsInput = await this.createManagedComponent('TagInput', null, {
                    instanceId: `skill-tools-${this.config.instanceId}`,
                    optionsSourceRpc: 'SkillRpc.getAvailableTools',
                    placeholder: 'Add tools...',
                    disabled: this.isReadOnly
                });
            }
            const toolsEl = await this.toolsInput.render();
            content.querySelector('#tools-input-container').appendChild(toolsEl);
            this.toolsInput.setValue(this.skillData.allowedTools || []);
        }

        targetContainer.appendChild(content);
    }

    async saveSkill() {
        this.extractCurrentTabData();

        if (!this.skillData.name || !this.skillData.description || !this.skillData.promptTemplate) {
            this.appClient.notifier.error("Name, Description, and Prompt Template are required.");
            return;
        }

        try {
            this.appClient.notifier.showProgress({ message: "Saving skill..." });

            if (this.isCreateMode) {
                const result = await this.appClient.rpcClient.call('SkillRpc.create', this.skillData);
                this.appClient.notifier.success("Skill created successfully.");
                this.appClient.layoutEngine.navigate(`/admin/skill-config?skillId=${result._id}`);
            } else {
                const result = await this.appClient.rpcClient.call('SkillRpc.update', this.skillData);
                this.appClient.notifier.success("Skill updated successfully.");
                this.skillData = result;
                this.originalData = JSON.stringify(result);
            }
        } catch (error) {
            this.appClient.notifier.error(`Save failed: ${error.message}`);
        } finally {
            this.appClient.notifier.hideProgress();
        }
    }
}